import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'redux/store';
import { callEndpoint } from 'redux/global';
import { IResponse } from 'types';

import { RecordFoundationFlags } from './recordCatalog';
import { ErpOperation } from './operationsCatalog';
import { isHiddenSystemEmployee, recordRowPhotoSrc } from './systemBot';
import {
  CustomMasterDataPayload,
  sanitizeCustomMasterDataPayload,
} from './customMasterData';

export type ErpRecord = {
  id: string
  name: string
  kind: string
  parentId: string | null
  parentName: string | null
  parentKind: string | null
  /** Employee id that created this master-data row. */
  createdBy?: string
  fields: Record<string, string>
  /** Face photo for employee chips, and creator photo for forms — same hover enlarge as /logs. */
  photoUrl?: string
  inviteToken?: string
  createdAt?: string
  updatedAt?: string
};

export type ErpDirectivePdfMeta = {
  gcsPath: string
  fileName: string
  sizeBytes: number
  contentType: string
};

export type ErpDirective = {
  id: string
  title: string
  description: string
  pdfMeta: ErpDirectivePdfMeta | null
  createdBy?: string
  createdAt?: string
  updatedAt?: string
};

export type ReservedRecordCode = {
  kind: string
  code: string
};

interface RecordsState {
  records: ErpRecord[]
  /** Soft-deleted + live codes/employee numbers — Generate must never reuse these. */
  reservedCodes: ReservedRecordCode[]
  foundation: RecordFoundationFlags | null
  customMasterData: CustomMasterDataPayload
  operations: ErpOperation[]
  /** All OP codes including soft-deleted. */
  usedOperationCodes: string[]
  directives: ErpDirective[]
  isLoading: boolean
  customMasterDataLoading: boolean
  operationsLoading: boolean
  directivesLoading: boolean
}

const emptyCustomMasterData = (): CustomMasterDataPayload => ({ groups: [], types: [] });

const initialState: RecordsState = {
  records: [],
  reservedCodes: [],
  foundation: null,
  customMasterData: emptyCustomMasterData(),
  operations: [],
  usedOperationCodes: [],
  directives: [],
  isLoading: true,
  customMasterDataLoading: true,
  operationsLoading: true,
  directivesLoading: true,
};

const recordsSlice = createSlice({
  name: 'records',
  initialState,
  reducers: {
    setErpRecords: (state, action: PayloadAction<ErpRecord[]>) => {
      state.records = action.payload;
    },
    setReservedRecordCodes: (state, action: PayloadAction<ReservedRecordCode[]>) => {
      state.reservedCodes = action.payload;
    },
    setRecordFoundation: (state, action: PayloadAction<RecordFoundationFlags | null>) => {
      state.foundation = action.payload;
    },
    setCustomMasterData: (state, action: PayloadAction<CustomMasterDataPayload>) => {
      state.customMasterData = action.payload;
    },
    setErpRecordsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setCustomMasterDataLoading: (state, action: PayloadAction<boolean>) => {
      state.customMasterDataLoading = action.payload;
    },
    setErpOperations: (state, action: PayloadAction<ErpOperation[]>) => {
      state.operations = action.payload;
    },
    setUsedOperationCodes: (state, action: PayloadAction<string[]>) => {
      state.usedOperationCodes = action.payload;
    },
    setErpOperationsLoading: (state, action: PayloadAction<boolean>) => {
      state.operationsLoading = action.payload;
    },
    setErpDirectives: (state, action: PayloadAction<ErpDirective[]>) => {
      state.directives = action.payload;
    },
    setErpDirectivesLoading: (state, action: PayloadAction<boolean>) => {
      state.directivesLoading = action.payload;
    },
  },
});

export const {
  setErpRecords,
  setReservedRecordCodes,
  setRecordFoundation,
  setCustomMasterData,
  setErpRecordsLoading,
  setCustomMasterDataLoading,
  setErpOperations,
  setUsedOperationCodes,
  setErpOperationsLoading,
  setErpDirectives,
  setErpDirectivesLoading,
} = recordsSlice.actions;

export const selectErpRecords = (state: RootState) => state.records.records;
export const selectReservedRecordCodes = (state: RootState) => state.records.reservedCodes;
export const selectUsedOperationCodes = (state: RootState) => state.records.usedOperationCodes;
export const selectRecordFoundation = (state: RootState) => state.records.foundation;
export const selectCustomMasterData = (state: RootState) => state.records.customMasterData;
export const selectErpRecordsLoading = (state: RootState) => state.records.isLoading;
export const selectCustomMasterDataLoading = (state: RootState) => state.records.customMasterDataLoading;
export const selectErpOperations = (state: RootState) => state.records.operations;
export const selectErpOperationsLoading = (state: RootState) => state.records.operationsLoading;
export const selectErpDirectives = (state: RootState) => state.records.directives;
export const selectErpDirectivesLoading = (state: RootState) => state.records.directivesLoading;

export const fetchCustomMasterData = () => async (dispatch: Function): Promise<IResponse> => {
  dispatch(setCustomMasterDataLoading(true));
  const res: IResponse = await dispatch(callEndpoint({
    api: 'getcustommasterdata',
    method: 'POST',
    body: {},
  }));
  dispatch(setCustomMasterDataLoading(false));
  if (res.status === 'success') {
    dispatch(setCustomMasterData(sanitizeCustomMasterDataPayload(res.data)));
  }
  return res;
};

export const fetchErpRecords = () => async (dispatch: Function): Promise<IResponse> => {
  dispatch(setErpRecordsLoading(true));
  const res: IResponse = await dispatch(callEndpoint({
    api: 'getrecords',
    method: 'POST',
    body: {},
  }));
  dispatch(setErpRecordsLoading(false));
  if (res.status === 'success') {
    const payload = res.data;
    const rows = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.records) ? payload.records : []);
    dispatch(setErpRecords(rows.map((row: ErpRecord) => {
      const photoKinds = new Set(['employee', 'management', 'form']);
      if (!photoKinds.has(row.kind)) return row;
      return { ...row, photoUrl: recordRowPhotoSrc(row) };
    }).filter((row: ErpRecord) => !(
      row.kind === 'employee' && isHiddenSystemEmployee(row.id, row.fields, row.name)
    ))));
    dispatch(setRecordFoundation(
      Array.isArray(payload) ? null : (payload?.foundation || null),
    ));
    const reservedRaw = Array.isArray(payload) ? [] : (payload?.reservedCodes || []);
    dispatch(setReservedRecordCodes(
      (Array.isArray(reservedRaw) ? reservedRaw : [])
        .map((row: { kind?: string, code?: string }) => ({
          kind: String(row?.kind || '').trim(),
          code: String(row?.code || '').trim(),
        }))
        .filter((row: ReservedRecordCode) => row.kind && row.code),
    ));
  }
  return res;
};

export const saveCustomMasterDataGroup = (group: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'savecustommasterdatagroup',
    method: 'POST',
    body: { group },
  }));
  if (res.status === 'success') await dispatch(fetchCustomMasterData());
  return res;
};

export const saveCustomMasterDataType = (typeDef: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'savecustommasterdatatype',
    method: 'POST',
    body: { typeDef },
  }));
  if (res.status === 'success') await dispatch(fetchCustomMasterData());
  return res;
};

export const saveCustomMasterDataFromTemplate = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'savecustommasterdatafromtemplate',
    method: 'POST',
    body,
  }));
  if (res.status === 'success') await dispatch(fetchCustomMasterData());
  return res;
};

export const addCustomMasterDataRecord = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'addcustommasterdatarecord',
    method: 'POST',
    body,
  }));
  if (res.status === 'success') await dispatch(fetchErpRecords());
  return res;
};

export const updateCustomMasterDataRecord = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'updatecustommasterdatarecord',
    method: 'POST',
    body,
  }));
  if (res.status === 'success') await dispatch(fetchErpRecords());
  return res;
};

export const removeCustomMasterDataRecord = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'removecustommasterdatarecord',
    method: 'POST',
    body,
  }));
  if (res.status === 'success') await dispatch(fetchErpRecords());
  return res;
};

export const addErpRecord = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'addrecord',
  method: 'POST',
  body,
}));

/** Org-wide uniqueness — getrecords may hide employees the caller cannot see. */
export const checkEmployeeEmailAvailable = (
  email: string,
  exceptId?: string,
) => async (dispatch: Function): Promise<IResponse> => dispatch(callEndpoint({
  api: 'checkemployeeemail',
  method: 'POST',
  body: { email, exceptId: exceptId || undefined },
}));

export const updateErpRecord = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'updaterecord',
  method: 'POST',
  body,
}));

/** Signed PUT for custom master private_picture — returns uploadUrl + valueJson metadata. */
export const prepareCustomMasterPrivatePictureUpload = (body: {
  recordId?: string
  fieldKey: string
  fileName: string
  bytes: number
  contentType?: string
}) => async (dispatch: Function): Promise<IResponse> => dispatch(callEndpoint({
  api: 'preparecustommasterprivatepictureupload',
  method: 'POST',
  body,
}));

export const removeErpRecord = (
  id: string,
  extra?: Record<string, unknown>,
) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'removerecord',
  method: 'POST',
  body: { id, ...(extra || {}) },
}));

/** Move every employee under one designation onto another designation. */
export const rotateDesignationEmployees = (body: {
  fromDesignationId: string,
  toDesignationId: string,
}) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'rotatedesignationemployees',
  method: 'POST',
  body,
}));

export const resendEmployeeInvite = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'resendemployeeinvite',
  method: 'POST',
  body,
}));

export const resendSupplierInvite = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => dispatch(callEndpoint({
  api: 'resendsupplierinvite',
  method: 'POST',
  body,
}));

const sanitizeOperation = (raw: Record<string, unknown>): ErpOperation => ({
  id: String(raw.id || ''),
  name: String(raw.name || ''),
  code: String(raw.code || ''),
  scopeKind: String(raw.scopeKind || raw.scope_kind || '') as ErpOperation['scopeKind'],
  scopeId: String(raw.scopeId || raw.scope_id || ''),
  workOrderId: String(raw.workOrderId || raw.work_order_id || ''),
  operationType: String(raw.operationType || raw.operation_type || '') as ErpOperation['operationType'],
  expiryMode: String(raw.expiryMode || raw.expiry_mode || 'indefinite') as ErpOperation['expiryMode'],
  expiresAt: raw.expiresAt || raw.expires_at ? String(raw.expiresAt || raw.expires_at) : null,
  isSystem: raw.isSystem === true || raw.is_system === true,
  data: raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? raw.data as ErpOperation['data']
    : {},
  createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
  updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
});

export const fetchErpOperations = () => async (dispatch: Function): Promise<IResponse> => {
  dispatch(setErpOperationsLoading(true));
  const res: IResponse = await dispatch(callEndpoint({
    api: 'getoperations',
    method: 'POST',
    body: {},
  }));
  dispatch(setErpOperationsLoading(false));
  if (res.status === 'success') {
    const rows = Array.isArray(res.data?.operations) ? res.data.operations : [];
    dispatch(setErpOperations(rows.map((row: Record<string, unknown>) => sanitizeOperation(row))));
    const used = Array.isArray(res.data?.usedCodes) ? res.data.usedCodes : [];
    dispatch(setUsedOperationCodes(
      used.map((code: unknown) => String(code || '').trim()).filter(Boolean),
    ));
  }
  return res;
};

export const saveErpOperation = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'saveoperation',
    method: 'POST',
    // path = app origin so dispatched work-order emails link back to /form/:token
    body: { ...body, path: typeof window !== 'undefined' ? window.location.origin : undefined },
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpOperations());
  }
  return res;
};

export const deleteErpOperation = (id: string) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'deleteoperation',
    method: 'POST',
    body: { id },
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpOperations());
  }
  return res;
};

export const revokeErpOperation = (id: string) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'revokeoperation',
    method: 'POST',
    body: { id },
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpOperations());
  }
  return res;
};

export const redispatchErpOperation = (id: string) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'redispatchoperation',
    method: 'POST',
    body: {
      id,
      path: typeof window !== 'undefined' ? window.location.origin : undefined,
    },
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpOperations());
  }
  return res;
};

const sanitizeDirective = (raw: Record<string, unknown>): ErpDirective => {
  const pdfRaw = raw.pdfMeta ?? raw.pdf_meta;
  let pdfMeta: ErpDirectivePdfMeta | null = null;
  if (pdfRaw && typeof pdfRaw === 'object' && !Array.isArray(pdfRaw)) {
    const pack = pdfRaw as Record<string, unknown>;
    const gcsPath = String(pack.gcsPath || '').trim();
    if (gcsPath) {
      pdfMeta = {
        gcsPath,
        fileName: String(pack.fileName || 'directive.pdf'),
        sizeBytes: Math.max(0, Number(pack.sizeBytes) || 0),
        contentType: String(pack.contentType || 'application/pdf'),
      };
    }
  }
  return {
    id: String(raw.id || ''),
    title: String(raw.title || ''),
    description: String(raw.description || ''),
    pdfMeta,
    createdBy: raw.createdBy ? String(raw.createdBy) : undefined,
    createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
  };
};

export const fetchErpDirectives = () => async (dispatch: Function): Promise<IResponse> => {
  dispatch(setErpDirectivesLoading(true));
  const res: IResponse = await dispatch(callEndpoint({
    api: 'getdirectives',
    method: 'POST',
    body: {},
  }));
  dispatch(setErpDirectivesLoading(false));
  if (res.status === 'success') {
    const rows = Array.isArray(res.data?.directives) ? res.data.directives : [];
    dispatch(setErpDirectives(rows.map((row: Record<string, unknown>) => sanitizeDirective(row))));
  }
  return res;
};

export const saveErpDirective = (body: Record<string, unknown>) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'savedirective',
    method: 'POST',
    body,
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpDirectives());
  }
  return res;
};

export const deleteErpDirective = (id: string) => async (
  dispatch: Function,
): Promise<IResponse> => {
  const res: IResponse = await dispatch(callEndpoint({
    api: 'deletedirective',
    method: 'POST',
    body: { id },
  }));
  if (res.status === 'success') {
    await dispatch(fetchErpDirectives());
  }
  return res;
};

export default recordsSlice.reducer;
